import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  DollarSign,
  TrendingUp,
  ShoppingCart,
  CreditCard,
  ArrowUpRight,
  Calendar,
  CheckCircle,
  Clock,
  AlertCircle,
  ArrowRight,
  ChevronRight,
  Wallet,
  ArrowDown,
  BookOpen
} from "lucide-react";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell
} from "recharts";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import PayoutLedgerDialog from "@/components/payouts/PayoutLedgerDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResellerEarnings() {
  const [data, setData] = useState(null);
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requestingPayout, setRequestingPayout] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState("");
  const [isPayoutModalOpen, setIsPayoutModalOpen] = useState(false);
  const [ledgerPayoutId, setLedgerPayoutId] = useState(null);

  useEffect(() => {
    fetchEarnings();
    fetchPayoutHistory();
  }, []);

  const fetchEarnings = async () => {
    try {
      const response = await axios.get(`${API}/earnings/my`);
      setData(response.data);
    } catch (error) {
      console.error("Failed to fetch earnings:", error);
      toast.error("Failed to load earnings data");
    }
  };

  const fetchPayoutHistory = async () => {
    try {
      const response = await axios.get(`${API}/payouts/my`);
      setPayoutHistory(response.data || []);
    } catch (error) {
      console.error("Failed to fetch payout history:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestPayout = async () => {
    if (!payoutAmount || parseFloat(payoutAmount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    if (parseFloat(payoutAmount) > (data?.earnings?.available_balance || 0)) {
      toast.error("Amount exceeds available balance");
      return;
    }

    setRequestingPayout(true);
    try {
      await axios.post(`${API}/payouts/request`, { amount: parseFloat(payoutAmount) });
      toast.success("Payout request submitted successfully!");
      setIsPayoutModalOpen(false);
      setPayoutAmount("");
      fetchEarnings(); // Refresh data
      fetchPayoutHistory();
    } catch (error) {
      toast.error(error.response?.data?.message || error.response?.data?.detail || "Failed to submit payout request");
    } finally {
      setRequestingPayout(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="relative w-16 h-16">
          <div className="absolute inset-0 border-4 border-indigo-100 rounded-full"></div>
          <div className="absolute inset-0 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  const earnings = data?.earnings || {};
  const transactions = data?.recent_transactions || [];
  const chartData = data?.chart_data || [];

  const stats = [
    {
      label: "Total Revenue",
      value: `$${(earnings.total_sales || 0).toLocaleString()}`,
      icon: ShoppingCart,
      color: "blue",
      trend: "+12.5%",
      description: "Gross merchandise volume"
    },
    {
      label: "Net Earnings",
      value: `$${(earnings.total_earnings || 0).toLocaleString()}`,
      icon: TrendingUp,
      color: "emerald",
      trend: "+8.2%",
      description: "Total profit from markups"
    },
    {
      label: "Available Balance",
      value: `$${(earnings.available_balance || 0).toLocaleString()}`,
      icon: Wallet,
      color: "amber",
      description: "Ready for withdrawal",
      action: true
    },
    {
      label: "Pending Clearance",
      value: `$${(earnings.pending_balance || 0).toLocaleString()}`,
      icon: Clock,
      color: "slate",
      description: "Locked in processing"
    }
  ];

  return (
    <div className="space-y-8 animate-in fade-in duration-700 pb-12">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-slate-900">
            Financial Overview
          </h2>
          <p className="text-slate-500">Track your performance and manage payouts</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="px-4 py-2 bg-white border border-slate-200 rounded-xl shadow-sm text-sm font-medium text-slate-600 flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Last 30 Days
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <div key={index} className="group relative bg-white rounded-3xl p-6 border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300">
            <div className="flex justify-between items-start mb-4">
              <div className={cn(
                "w-12 h-12 rounded-2xl flex items-center justify-center transition-transform group-hover:scale-110 duration-300",
                stat.color === 'blue' ? "bg-blue-50 text-blue-600" :
                  stat.color === 'emerald' ? "bg-emerald-50 text-emerald-600" :
                    stat.color === 'amber' ? "bg-amber-50 text-amber-600" : "bg-slate-50 text-slate-600"
              )}>
                <stat.icon size={24} />
              </div>
              {stat.trend && (
                <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">
                  {stat.trend}
                </span>
              )}
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-slate-400 uppercase tracking-wider">{stat.label}</p>
              <p className="text-2xl font-bold text-slate-900">
                {stat.value}
              </p>
              <p className="text-xs text-slate-400">{stat.description}</p>
            </div>
            {stat.action && (
              <Button
                onClick={() => setIsPayoutModalOpen(true)}
                disabled={earnings.available_balance <= 0}
                className="mt-4 w-full h-10 rounded-xl bg-amber-600 hover:bg-amber-700 text-white shadow-lg shadow-amber-200/50 text-xs font-bold uppercase tracking-widest"
              >
                Withdraw Funds <ArrowRight size={14} className="ml-2" />
              </Button>
            )}
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Chart Section */}
        <div className="lg:col-span-2 space-y-8">
          <div className="bg-white rounded-[2rem] p-8 border border-slate-100 shadow-sm">
            <div className="flex items-center justify-between mb-8">
              <h3 className="text-xl font-bold text-slate-900">
                Performance Analytics
              </h3>
              <div className="flex gap-2">
                <div className="flex items-center gap-2 text-xs font-bold text-slate-400 uppercase tracking-widest">
                  <div className="w-3 h-3 bg-indigo-500 rounded-full" /> Earnings
                </div>
                <div className="flex items-center gap-2 text-xs font-bold text-slate-400 uppercase tracking-widest">
                  <div className="w-3 h-2 bg-indigo-100 rounded-full" /> Total Sales
                </div>
              </div>
            </div>
            <div className="h-[350px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="colorEarnings" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis
                    dataKey="date"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 600 }}
                    tickFormatter={(val) => val.split('-').slice(1).join('/')}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 600 }}
                    tickFormatter={(val) => `$${val}`}
                  />
                  <Tooltip
                    contentStyle={{ border: 'none', borderRadius: '16px', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                    itemStyle={{ fontSize: '12px', fontWeight: 600 }}
                  />
                  <Area
                    type="monotone"
                    dataKey="earnings"
                    stroke="#6366f1"
                    strokeWidth={4}
                    fillOpacity={1}
                    fill="url(#colorEarnings)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white rounded-[2rem] p-8 border border-slate-100 shadow-sm">
            <h3 className="text-xl font-bold text-slate-900 mb-6">
              Recent Transactions
            </h3>
            <div className="space-y-4">
              {transactions.length > 0 ? (
                transactions.map((txn, i) => (
                  <div key={txn.transaction_id} className="group flex items-center justify-between p-4 rounded-2xl hover:bg-slate-50 transition-colors border border-transparent hover:border-slate-100">
                    <div className="flex items-center gap-4">
                      <div className={cn(
                        "w-12 h-12 rounded-xl flex items-center justify-center shadow-sm",
                        txn.status === 'delivered' ? "bg-emerald-100 text-emerald-600" : "bg-blue-100 text-blue-600"
                      )}>
                        <DollarSign size={20} />
                      </div>
                      <div>
                        <p className="font-bold text-slate-900 text-sm">Order #{txn.transaction_id.slice(-8).toUpperCase()}</p>
                        <p className="text-xs text-slate-400">{formatDate(txn.created_at)}</p>
                      </div>
                    </div>
                    <div className="text-right flex flex-col items-end gap-1">
                      <p className="font-bold text-emerald-600">+${txn.markup_total.toFixed(2)}</p>
                      <div className={cn(
                        "flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider",
                        txn.status === 'delivered' ? "bg-emerald-50 text-emerald-700" : "bg-blue-50 text-blue-700"
                      )}>
                        {txn.status === 'delivered' ? <CheckCircle size={10} /> : <Clock size={10} />}
                        {txn.status}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
                    <AlertCircle className="text-slate-300" size={32} />
                  </div>
                  <p className="text-slate-500 font-medium">No transactions found</p>
                  <p className="text-xs text-slate-400">Your sales activity will appear here.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar Cards */}
        <div className="space-y-8">
          {/* Commission Guide */}
          <div className="bg-gradient-to-br from-indigo-600 to-indigo-800 rounded-[2rem] p-8 text-white shadow-xl shadow-indigo-200">
            <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center mb-6 backdrop-blur-md">
              <TrendingUp size={24} />
            </div>
            <h3 className="text-xl font-bold mb-4">Profit Breakdown</h3>
            <p className="text-indigo-100 text-sm leading-relaxed mb-6">
              You earn 100% of the markup you set in your store. The higher your multiplier, the greater your commission on every sale.
            </p>
            <div className="space-y-4 pt-6 border-t border-white/10">
              <div className="flex justify-between items-center text-sm">
                <span className="opacity-70">Avg. Margin</span>
                <span className="font-bold">25%</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="opacity-70">Next Payout</span>
                <span className="font-bold">${(earnings.available_balance || 0).toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Payout History */}
          <div className="bg-white rounded-[2rem] p-8 border border-slate-100 shadow-sm">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-slate-900">Payout History</h3>
              <Badge variant="outline" className="text-[10px] uppercase tracking-widest font-bold">Past 30 Days</Badge>
            </div>
            <div className="space-y-4">
              {payoutHistory.length > 0 ? (
                payoutHistory.map((payout) => (
                  <div key={payout.payout_id} className="flex items-center justify-between p-4 rounded-2xl bg-slate-50/50 border border-slate-100 hover:bg-white hover:shadow-md transition-all group">
                    <div className="flex items-center gap-4">
                      <div className={cn(
                        "w-10 h-10 rounded-xl flex items-center justify-center",
                        payout.status === 'completed' ? "bg-green-100 text-green-600" :
                          payout.status === 'pending' ? "bg-amber-100 text-amber-600" : "bg-slate-100 text-slate-600"
                      )}>
                        <CreditCard size={18} />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-slate-900 capitalize">{payout.status} Payout</p>
                        <p className="text-[10px] text-slate-400 font-medium">{formatDate(payout.created_at)}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-black text-slate-900">${payout.amount.toFixed(2)}</p>
                      <p className="text-[10px] text-slate-400 font-medium">{payout.method || 'Standard Transfer'}</p>
                      <button
                        type="button"
                        onClick={() => setLedgerPayoutId(payout.payout_id)}
                        className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold text-indigo-600 hover:text-indigo-700 hover:underline"
                        data-testid={`view-ledger-${payout.payout_id}`}
                      >
                        <BookOpen size={11} /> View ledger
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <p className="text-xs text-slate-400 italic">No payout history yet.</p>
                </div>
              )}
            </div>
            {payoutHistory.length > 5 && (
              <Button variant="ghost" className="w-full mt-4 text-xs font-bold text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50/50 rounded-xl">
                View All History <ChevronRight size={14} className="ml-1" />
              </Button>
            )}
          </div>

          {/* Quick Support */}
          <div className="bg-slate-900 rounded-[2rem] p-8 text-white">
            <h3 className="text-lg font-bold mb-4">Need Help?</h3>
            <p className="text-slate-400 text-xs mb-6">Contact our financial team if you have questions about your earnings or payouts.</p>
            <Button variant="outline" className="w-full bg-transparent border-slate-700 hover:bg-slate-800 text-white rounded-xl">
              Financial Support
            </Button>
          </div>
        </div>
      </div>

      {/* Ledger entries behind one of my payouts */}
      <PayoutLedgerDialog
        open={!!ledgerPayoutId}
        onClose={() => setLedgerPayoutId(null)}
        payoutId={ledgerPayoutId}
        payoutKind="reseller"
        viewer="reseller"
      />

      {/* Payout Modal */}
      <Dialog open={isPayoutModalOpen} onOpenChange={setIsPayoutModalOpen}>
        <DialogContent className="max-w-md rounded-[2rem]">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center text-amber-600">
                <Wallet size={20} />
              </div>
              Request Payout
            </DialogTitle>
            <DialogDescription className="pt-2 text-slate-500">
              Your funds will be transferred to your registered payment method.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-6">
            <div className="p-6 bg-slate-50 rounded-2xl border border-slate-100">
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">Available Balance</span>
                <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-md">Verified</span>
              </div>
              <p className="text-3xl font-bold text-slate-900">${(earnings.available_balance || 0).toLocaleString()}</p>
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Withdraw Amount</Label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 font-bold text-slate-400">$</span>
                <Input
                  type="number"
                  placeholder="0.00"
                  value={payoutAmount}
                  onChange={(e) => setPayoutAmount(e.target.value)}
                  className="h-14 pl-8 rounded-xl border-slate-200 text-lg font-bold"
                />
              </div>
              <div className="flex gap-2 pt-2">
                {[0.25, 0.5, 1].map(pct => (
                  <button
                    key={pct}
                    onClick={() => setPayoutAmount(((earnings.available_balance || 0) * pct).toFixed(2))}
                    className="px-3 py-1 bg-slate-100 hover:bg-slate-200 rounded-lg text-xs font-bold text-slate-600"
                  >
                    {pct === 1 ? 'Max' : `${pct * 100}%`}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter className="gap-3">
            <Button
              variant="outline"
              onClick={() => setIsPayoutModalOpen(false)}
              className="flex-1 h-12 rounded-xl border-slate-200"
            >
              Cancel
            </Button>
            <Button
              onClick={handleRequestPayout}
              disabled={requestingPayout || !payoutAmount || parseFloat(payoutAmount) <= 0}
              className="flex-1 h-12 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              {requestingPayout ? <Clock className="animate-spin mr-2" size={16} /> : <CheckCircle size={16} className="mr-2" />}
              {requestingPayout ? 'Processing...' : 'Confirm Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
